import React, {useContext, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import {Btn, ColorTheme, Icon, colorTheme} from '../../../ui-toolkit/index';
import {useContextMenu2} from './useContextMenu';

const ContextMenuContext = React.createContext({close: null});

const ContextMenu = ({outerRef, onOpen, children}) => {
    const menuRef = useRef(null);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const {xPos, yPos, menu, close} = useContextMenu2(outerRef, width, height, {onOpen});

    if (!menu) return null;
    return ReactDOM.createPortal(
        <ContextMenuContext.Provider value={{close}}>
            <ColorTheme>
                <Wrapper ref={menuRef} style={{top: yPos, left: xPos}} onClick={(e) => e.stopPropagation()}>
                    <MeasureWrapper measureRef={menuRef} setWidth={setWidth} setHeight={setHeight}>
                        {children}
                    </MeasureWrapper>
                </Wrapper>
            </ColorTheme>
        </ContextMenuContext.Provider>,
        document.getElementById('contextMenuPortal'),
    );
};

function MeasureWrapper({measureRef, children, setWidth, setHeight}) {
    useEffect(() => {
        if (measureRef.current != null) {
            // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded#:~:text=It%27s%20an%20old%20question%20but%20it%20still%20might%20be%20helpful%20to%20someone.%20You%20can%20avoid%20this%20error%20by%20wrapping%20the%20callback%20in%20requestAnimationFrame
            // this code apparently makes the application crash in local, the error is not thrown in testing and up.
            // The fix explanation is in the link.
            const resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame((entries) => {
                    if (!Array.isArray(entries) || !entries.length) {
                        return;
                    }
                    setWidth(measureRef.current?.offsetWidth);
                    setHeight(measureRef.current?.offsetHeight);
                });
            });
            resizeObserver.observe(measureRef.current);
            return () => resizeObserver.disconnect();
        }
    }, [measureRef.current]);

    return children;
}

const ContextMenuItem = ({icon, label, onClick}) => {
    const {close} = useContext(ContextMenuContext);
    return (
        <li>
            <Btn
                link
                color="primary"
                onClick={() => {
                    onClick();
                    close();
                }}>
                <Icon name={icon} type="sm" />
                {label}
            </Btn>
        </li>
    );
};

const ContextMenuSection = styled.div``;

const ContextMenuTextItem = ({label}) => (
    <li>
        <span>{label}</span>
    </li>
);

const Wrapper = styled.ul`
    ${colorTheme({
        '--tooltip-btn-text-color-hover   ': {dark: 'var(--white-00)       ', light: 'var(--primary-dark)'},
    })};

    z-index: var(--z-index-table-tooltip);
    position: fixed;
    background-color: var(--container-background-color-100);
    padding: 0;
    border: none;
    box-shadow: var(--container-shadow-color-300);
    border-radius: 4px;
    li {
        list-style: none;
        ${Btn} {
            padding: 6px 12px;
            text-align: left;
            align-items: center;
            display: flex;
            width: 100%;
            justify-content: flex-start;
            border-radius: 0;
            > span {
                margin-right: 4px;
                display: contents;
            }
            &:hover {
                background: var(--container-background-hover-color-100);
                color: var(--tooltip-btn-text-color-hover);
            }
        }
        > span {
            display: inline-block;
            padding: 6px 12px;
            width: 100%;
            text-align: left;
            align-items: center;
        }
    }

    ${ContextMenuSection} + ${ContextMenuSection} {
        border-top: 1px solid var(--container-border-color-200);
    }
`;

ContextMenu.Item = ContextMenuItem;
ContextMenu.TextItem = ContextMenuTextItem;
ContextMenu.Section = ContextMenuSection;

export default ContextMenu;
